import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useHeroParallax } from '@/hooks/useHeroParallax';
import { useReveal } from '@/hooks/useReveal';
import { GUIDE_ICON } from '@/components/GuideIcons';
import HeroCrumbs from '@/components/HeroCrumbs';
import { CASE_POSTS } from '@/data/mockData';
import { CASE_CONTENT, type CaseSection } from '@/data/casesContent';
import s from './CaseDetail.module.scss';

// 설치 사례 상세(/cases/:slug) — [2026-09-09 사용자] evs.com/applications식:
//   ★히어로 = 다른 페이지와 동일한 표준 히어로(지정 설치사례 이미지·사이즈·장식).
//   ★케이스 카테고리·제목·요약·날짜 = 본문 '최상위 섹션'(히어로가 아님).
//   섹션별 좌우 교차 이미지+텍스트 · 의미별 SVG 아이콘 · 강조. 원문(네이버 블로그) 재구성.
//   데이터=casesContent.ts(섹션 그룹). 안전=시안 / 환경=그린(--tc). 뒤로가기 스크롤 복원=Link.
// 히어로: 안전=기본 그대로, 환경 도메인만 전용 이미지(2026-09-09 사용자). CasesList와 동일 규칙.
const CASES_HERO = '/images/hero/cases-hero.jpg';
const CASES_HERO_ENV = '/images/hero/cases-hero-env.jpg';
const M1 = String.fromCharCode(1); // 강조 시작 마커
const M2 = String.fromCharCode(2); // 강조 끝 마커
const SPLIT = new RegExp('[' + M1 + M2 + ']');

function Rich({ text }: { text: string }) {
  const parts = text.split(SPLIT); // 마커 사이(홀수 인덱스)만 <b>
  return <>{parts.map((p, i) => (i % 2 === 1 ? <b key={i}>{p}</b> : <span key={i}>{p}</span>))}</>;
}

function SectionView({ sec, i, onImg }: { sec: CaseSection; i: number; onImg: (src: string) => void }) {
  const ref = useReveal<HTMLDivElement>();
  const alt = i % 2 === 1;
  const hasImg = sec.images.length > 0;
  const hideOnErr = (e: React.SyntheticEvent<HTMLImageElement>) => { (e.currentTarget.closest('figure') as HTMLElement).style.display = 'none'; };

  if (!sec.title) {
    return (
      <div className={`${s.intro} anim-scroll`} ref={ref}>
        {sec.paras.map((p, k) => <p key={k} className={s.introP}><Rich text={p} /></p>)}
        {sec.images.length > 0 && (
          <div className={`${s.gallery} ${sec.images.length === 1 ? s.gallerySolo : ''}`}>
            {sec.images.map((src, k) => (
              <figure key={k}><img src={src} alt="현장" loading="lazy" onError={hideOnErr} onClick={() => onImg(src)} /></figure>
            ))}
          </div>
        )}
      </div>
    );
  }

  const Text = (
    <div className={s.text}>
      <span className={s.icon}>{GUIDE_ICON[sec.icon] || GUIDE_ICON.solution}</span>
      <h2 className={s.h2}>{sec.title}</h2>
      {sec.paras.map((p, k) => <p key={k} className={s.p}><Rich text={p} /></p>)}
      {sec.specs.length > 0 && (
        <ul className={s.specs}>{sec.specs.map((sp, k) => <li key={k}>{sp}</li>)}</ul>
      )}
    </div>
  );

  if (!hasImg) return <div className={`${s.rowText} anim-scroll`} ref={ref}>{Text}</div>;

  const Media = (
    <div className={`${s.media} ${sec.images.length > 1 ? s.mediaMulti : ''}`}>
      {sec.images.map((src, k) => (
        <figure key={k}>
          <img src={src} alt={sec.title} loading="lazy" onError={hideOnErr} onClick={() => onImg(src)} />
        </figure>
      ))}
    </div>
  );

  // 본문(문단·사양) 없는 섹션 = 이미지가 자체 설명 인포그래픽 → 좌우 분할(허전) 대신 제목 + 풀폭 이미지.
  const noBody = sec.paras.length === 0 && sec.specs.length === 0;
  if (noBody) {
    return (
      <div className={`${s.feature} anim-scroll`} ref={ref}>
        <div className={s.featHead}>
          <span className={s.iconSm}>{GUIDE_ICON[sec.icon] || GUIDE_ICON.solution}</span>
          <h2 className={s.featTitle}>{sec.title}</h2>
        </div>
        <div className={`${s.featMedia} ${sec.images.length > 1 ? s.gallery : ''}`}>
          {sec.images.map((src, k) => (
            <figure key={k}><img src={src} alt={sec.title} loading="lazy" onError={hideOnErr} onClick={() => onImg(src)} /></figure>
          ))}
        </div>
      </div>
    );
  }

  return <div className={`${s.row} ${alt ? s.alt : ''} anim-scroll`} ref={ref}>{Media}{Text}</div>;
}

export default function CaseDetail() {
  const { slug } = useParams();
  const heroRef = useHeroParallax<HTMLDivElement>();
  const metaRef = useReveal<HTMLDivElement>();
  const post = CASE_POSTS.find((p) => p.slug === slug);
  const sections = slug ? CASE_CONTENT[slug] : undefined;
  const [lightbox, setLightbox] = useState<string | null>(null); // 이미지 클릭 확대(팝업 아닌 인라인 오버레이)
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const start = performance.now();
    let done = false;
    const finish = () => { if (done) return; done = true; const w = Math.max(0, 300 - (performance.now() - start)); window.setTimeout(() => setLoading(false), w); };
    const cap = window.setTimeout(finish, 1000);
    const heroSrc = post?.domain === 'environment' ? CASES_HERO_ENV : CASES_HERO;
    const im = new Image(); im.onload = finish; im.onerror = finish; im.src = heroSrc; if (im.complete) finish();
    return () => window.clearTimeout(cap);
  }, [slug]);

  if (!post) {
    return (
      <main className={s.notFound}>
        <p>설치 사례를 찾을 수 없습니다.</p>
        <Link to="/cases" className={s.backBtn}>설치 사례 목록으로</Link>
      </main>
    );
  }

  const env = post.domain === 'environment';
  const domainLabel = env ? '환경' : '안전';
  const domainPosts = CASE_POSTS.filter((p) => p.domain === post.domain);
  const idx = domainPosts.findIndex((p) => p.slug === slug);
  const prev = idx > 0 ? domainPosts[idx - 1] : null;
  const next = idx < domainPosts.length - 1 ? domainPosts[idx + 1] : null;

  return (
    <main className={`${s.page} ${env ? s.tEnv : s.tSafety}`}>
      <div className={`${s.loader} ${loading ? '' : s.loaderDone}`} aria-hidden>
        <span className={s.ring} /><span className={s.brand}>EVS</span>
      </div>

      {/* 표준 히어로 — 지정 설치사례 이미지(다른 페이지와 동일 사이즈·장식) */}
      <header className={s.hero}>
        <div ref={heroRef} className={s.heroBg} style={{ backgroundImage: `url(${CASES_HERO})` }} />
        <div className={s.heroOverlay} />
        <HeroCrumbs items={['HOME', '설치 사례', `${domainLabel} 사례`]} /> {/* [2026-10-11] 메뉴 라벨 통일 + 최종 단계(도메인 사례) 표기 */}
        <div className={s.heroInner}>
          <p className={s.heroEyebrow}>INSTALLATION CASES</p>
          <h2 className={s.heroTitle}>{domainLabel} 설치 사례</h2>
        </div>
      </header>

      <div className={s.sections}>
        {/* 최상위 섹션 — 케이스 카테고리·제목·요약·날짜 */}
        <div className={`${s.metaHead} anim-scroll`} ref={metaRef}>
          <p className={s.metaCat}>
            <Link to={`/cases?tab=${post.domain}`} className={s.crumb}>{domainLabel}</Link> · {post.category} 사례
          </p>
          <h1 className={s.metaTitle}>{post.title}</h1>
          <p className={s.metaSummary}>{post.summary}</p>
          <p className={s.metaDate}>EVS <span>·</span> {post.date}</p>
        </div>

        {sections && sections.length
          ? sections.map((sec, i) => <SectionView key={i} sec={sec} i={i} onImg={setLightbox} />)
          : <p className={s.soon}>이 사례의 상세 내용은 준비 중입니다.</p>}
      </div>

      {/* CTA */}
      <div className={s.ctaWrap}>
        <div className={s.cta}>
          <div>
            <p className={s.ctaKick}>{env ? 'EVS ENVIRONMENT IoT' : 'EVS SAFETY IoT'}</p>
            <b className={s.ctaTitle}>{env ? '대기배출 IoT 측정기기 · IoT GateWay (EVS-G200)' : 'AI 온열질환 IoT 체감온도계'}</b>
            <span className={s.ctaSub}>{env ? '방지시설 실시간 원격 감시 · 그린링크 자동 전송 · 원스톱 설치·A/S' : '실시간 WBGT 관제 · 법정 기록 자동 저장 · SaaS 통합관제'}</span>
          </div>
          <Link to={env ? '/environment#products' : '/safety#products'} className={s.ctaBtn}>제품 자세히 보기 →</Link>
        </div>
      </div>

      <div className={s.foot}>
        <div className={s.nav2}>
          {prev ? <Link to={`/cases/${prev.slug}`} className={s.navItem}><span className={s.k}>‹ 이전 사례</span><b>{prev.title}</b></Link> : <span />}
          {next ? <Link to={`/cases/${next.slug}`} className={`${s.navItem} ${s.navNext}`}><span className={s.k}>다음 사례 ›</span><b>{next.title}</b></Link> : <span />}
        </div>
        <div className={s.backWrap}><Link to={`/cases?tab=${post.domain}`} className={s.backBtn}>← 설치 사례 전체 목록</Link></div>
      </div>

      {/* 이미지 확대 라이트박스(팝업창 아님, 인라인 오버레이) — 배경/닫기 버튼/ESC로 닫힘 */}
      {lightbox && (
        <div className={s.lightbox} onClick={() => setLightbox(null)} role="dialog" aria-modal="true">
          <button type="button" className={s.lbClose} aria-label="닫기" onClick={() => setLightbox(null)}>✕</button>
          <img src={lightbox} alt="확대 이미지" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </main>
  );
}
